/**
 * catalog-request-api — 백엔드 /api/admin/catalog-requests (hcltech-parts CatalogRequest.Routes · CLAUDE.md V장 "카탈로그 승격 — 요청함 모듈 이름" · 2026-10-14).
 *   요청함 한 줄 = catalog_part_pending 한 행 + 부모 요청 + 출처 product. 탭 다섯(requested 요청됨 · drafted 확인 대기 · on_hold 보류 · registered 컨펌 · rejected 반려).
 *   초안 저장(PUT) · 승격(POST promote) · 반려(POST reject) · 보류(POST hold) · 보류 풀기(POST release) · 메모(PUT notes). 스키마는 백엔드 CatalogRequest.Types 와 같은 철자 — 다르면 Zod 가 경계에서 막는다.
 */
import { z } from 'zod';
import { getJson, postJson, putJson } from './http';
import type { SlotFill } from './description-rule-api';

// ─── 열거 — 백엔드와 같은 철자 ───────────────────────────────────────
/** 요청함 탭 — 확인 대기 · 요청됨(옛 pending 값도 여기) · 지난 것(registered · rejected) */
export const E_CATALOG_REQUEST_FILTERS = ['requested', 'drafted', 'on_hold', 'registered', 'rejected', 'all'] as const;   // 'all' 전체 — 맨 뒤 탭(2026-10-20)   // 탭 순서 = 흐름 순서(요청됨 → 확인 대기 → 컨펌 · 반려). 기본은 확인 대기(사람 할 일)
export type  ECatalogRequestFilter    = typeof E_CATALOG_REQUEST_FILTERS[number];
export const E_REQUEST_PERIODS = ['all', 'week', 'month'] as const;   // 요청날짜 기간 — 서버와 같은 값(회의 2026-10-15 · 2026-10-19)
export type  ERequestPeriod    = typeof E_REQUEST_PERIODS[number];
export const REQUEST_PERIOD_WORD: Record<ERequestPeriod, string> = { all: '전체 기간', week: '최근 7일', month: '최근 30일' };

/** pending 한 행의 status — 마이그 307 의 값(pending 은 331 전의 옛 값) */
export const E_CATALOG_PENDING_STATUSES = ['pending', 'requested', 'drafted', 'on_hold', 'registered', 'rejected'] as const;   // on_hold 보류(2026-10-18)
export type  ECatalogPendingStatus    = typeof E_CATALOG_PENDING_STATUSES[number];

/** 초안이 어디서 왔나 */
export const E_DRAFT_SOURCES = ['bulletin', 'icecat', 'decoder', 'web_llm', 'manual'] as const;
export type  EDraftSource    = typeof E_DRAFT_SOURCES[number];

/** 카탈로그 표 17(부품 16 + chassis) — 백엔드 CatalogLoad.Types E_CATALOG_TABLES 와 같은 철자 · 순서 */
export const E_CATALOG_TABLES = [
  'chassis', 'cpu', 'memory', 'ssd', 'hdd', 'raid_controller', 'cache_battery', 'gpu',
  'network_adapter', 'hba', 'power_supply', 'rail_kit', 'riser', 'drive_tray', 'fan', 'heatsink', 'accessory',
] as const;
export type  ECatalogTable = typeof E_CATALOG_TABLES[number];

/** 한국말 — 탭 · 상태 · 출처 */
export const REQUEST_FILTER_WORD: Record<ECatalogRequestFilter, string> = { drafted: '확인 대기', requested: '요청됨', on_hold: '보류', registered: '컨펌', rejected: '반려', all: '전체' };
export const PENDING_STATUS_WORD: Record<ECatalogPendingStatus, string> = { pending: '요청됨', requested: '요청됨', drafted: '확인 대기', on_hold: '보류', registered: '컨펌', rejected: '반려' };
export const DRAFT_SOURCE_WORD:   Record<EDraftSource, string> = { bulletin: '문서', icecat: 'Icecat', decoder: '디코더', web_llm: '웹조사', manual: '사람' };
/** 카탈로그 표 → 화면의 카테고리 말 — 영어로(현민 2026-10-14 밤 "한글 · 영어 섞어 쓰면 더 헷갈림". 처음 한글(메모리 · 파워서플라이)은 같은 밤 뺌). 코드 말은 그대로 표 이름 */
export const CATALOG_TABLE_WORD: Record<ECatalogTable, string> = {
  chassis: 'Chassis', cpu: 'CPU', memory: 'Memory', ssd: 'SSD', hdd: 'HDD', raid_controller: 'RAID Controller', cache_battery: 'Cache Battery',
  gpu: 'GPU', network_adapter: 'NIC', hba: 'HBA', power_supply: 'Power Supply', rail_kit: 'Rail Kit', riser: 'Riser', drive_tray: 'Drive Tray',
  fan: 'Fan', heatsink: 'Heatsink', accessory: 'Accessory',
};
/** 표 이름 글 → 카테고리 말. 17 밖(옛 non_hw · cable …)은 글 그대로 */
export function catalogTableWord(raw: string): string {
  const found = E_CATALOG_TABLES.find((table) => table === raw);
  return found === undefined ? raw : CATALOG_TABLE_WORD[found];
}

// ─── 스키마 ─────────────────────────────────────────────────────────
/** 요청함 한 줄 — pending 한 행 + 부모 요청(requested_text · requested_by) + 출처 product. status_reason 은 시스템 글(루틴의 "왜" · 반려 이유 · 다시 조사), notes 는 사람의 운영 메모(2026-10-18) */
export const CatalogRequestViewSchema = z.object({
  pending_id: z.number().int(),
  request_id: z.string().nullable(),
  requested_text: z.string().nullable(),
  requested_by: z.string().nullable(),
  option_part: z.string().nullable(),
  catalog_table_name: z.string().nullable(),
  status: z.enum(E_CATALOG_PENDING_STATUSES),
  spec_draft: z.record(z.string(), z.unknown()),       // 초안 칸 — 키 = 대상 표 열 이름. 없으면 {}
  spec_evidence: z.record(z.string(), z.string()),     // 칸마다 근거 url. 없으면 {}. 모델 칸은 'model.<열>' 키
  model_draft: z.record(z.string(), z.unknown()).nullable(),   // 모델 표 칸 초안(344). 모델 FK 표가 아니면 null
  draft_source: z.enum(E_DRAFT_SOURCES).nullable(),
  status_reason: z.string().nullable(),
  notes: z.string().nullable(),
  product: z.object({ id: z.string(), hclt_number: z.string() }).nullable(),
  created_at: z.string().nullable(),
});
export type CatalogRequestView = z.infer<typeof CatalogRequestViewSchema>;

/** 카탈로그 전체 한 줄(2026-10-20) — 확정 행(v_catalog_part + id) 또는 열린 요청. status 는 요청만(확정 행은 null) */
export const E_CATALOG_LINE_KINDS = ['row', 'pending'] as const;
export type  ECatalogLineKind    = typeof E_CATALOG_LINE_KINDS[number];
export const CatalogLineViewSchema = z.object({
  kind: z.enum(E_CATALOG_LINE_KINDS),
  id: z.string(),
  table: z.string().nullable(),
  option_part: z.string().nullable(),
  vendor: z.string().nullable(),
  description: z.string().nullable(),
  status: z.enum(E_CATALOG_PENDING_STATUSES).nullable(),
});
export type CatalogLineView = z.infer<typeof CatalogLineViewSchema>;
export const CatalogLineViewListSchema = z.object({
  rows: z.array(CatalogLineViewSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type CatalogLineViewList = z.infer<typeof CatalogLineViewListSchema>;

export const CatalogRequestViewListSchema = z.object({
  rows: z.array(CatalogRequestViewSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});
export type CatalogRequestViewList = z.infer<typeof CatalogRequestViewListSchema>;

/** 폼이 그릴 칸 하나 — 대상 표의 열(DB 가 채우는 id · created_at · updated_at 은 빠져 있다). data_type 은 pg 이름 그대로, comment 는 DDL 주석 */
export const TableColumnViewSchema = z.object({
  name: z.string(),
  required: z.boolean(),
  data_type: z.string(),
  comment: z.string().nullable(),            // DDL 주석 — 한글 라벨(앞 문장) · 힌트(뒷말). 없으면 null
  numeric_precision: z.number().nullable(),  // NUMERIC(p,s) 의 전체 자릿수 — 제한 없는 NUMERIC · 정수 · 글 열은 null(parts V장 "소수 자리 넘침")
  numeric_scale: z.number().nullable(),      // NUMERIC(p,s) 의 소수 자리 — 위와 같다
});
export type TableColumnView = z.infer<typeof TableColumnViewSchema>;

/** 모델 칸 묶음 — 모델 FK 표(cpu · gpu · hba · network_adapter)만. key_column 은 부품 칸 쪽 열쇠 칸(model · series · chip_model) */
export const ModelColumnsViewSchema = z.object({
  table: z.string(),
  key_column: z.string(),
  rows: z.array(TableColumnViewSchema),
});
export type ModelColumnsView = z.infer<typeof ModelColumnsViewSchema>;

/** GET /columns/:table — 부품 칸 + 모델 칸(없으면 null) */
export const TableColumnsViewSchema = z.object({
  rows: z.array(TableColumnViewSchema),
  model: ModelColumnsViewSchema.nullable(),
});
export type TableColumnsView = z.infer<typeof TableColumnsViewSchema>;

/** GET /columns/:table/model?key= — 열쇠의 모델 행. row null = 없음(승격이 새로 만든다 — 모델 필수 칸이 다 차야) */
export const ModelRowViewSchema = z.object({
  table: z.string(),
  key: z.string(),
  row: z.record(z.string(), z.unknown()).nullable(),
});
export type ModelRowView = z.infer<typeof ModelRowViewSchema>;

/** 승격 한 번의 결과 */
export const PromoteResultSchema = z.object({
  table: z.string(),
  row_id: z.number().int(),
  event_type: z.enum(['insert', 'update']),            // 없어서 만들었나 · 있어서 사람이 고른 값으로 고쳤나(2026-10-20)
  hclt_product_id: z.string().nullable(),               // 같이 채운 product 중 첫째. 요청에 product 가 없으면 null
  products_filled: z.number().int(),                    // 같이 채운 product 수 — 이 pending 에 이어진 요청 전부(2026-10-20 밤)
});
export type PromoteResult = z.infer<typeof PromoteResultSchema>;

// ─── 승격 — 번호가 이미 있는 행이면 수정(2026-10-20. 백엔드 CatalogRequest.Types 와 같은 철자) ───
/** 카탈로그 번호 열 넷 — 요청함 초안이 찾는 번호 */
export const E_PART_NUMBER_COLUMNS = ['option_part', 'spare_part', 'assembly_part', 'factory_part'] as const;
export type  EPartNumberColumn     = typeof E_PART_NUMBER_COLUMNS[number];
/** 초안의 번호 넷 — 폼 칸 그대로. null = 안 찾음 */
export type DraftNumbers = Record<EPartNumberColumn, string | null>;

/** 번호 넷의 판정 — 새 행 · 그 행 수정 · 사람이 고름 */
export const E_DRAFT_MATCH_VERDICTS = ['new', 'edit', 'choose'] as const;
export type  EDraftMatchVerdict     = typeof E_DRAFT_MATCH_VERDICTS[number];

/** 번호 하나가 맞은 카탈로그 행 한 줄. 같은 행이 두 번호로 맞으면 두 줄 */
export const CatalogNumberMatchSchema = z.object({
  table: z.enum(E_CATALOG_TABLES),
  row_id: z.number().int(),
  option_part: z.string(),
  description: z.string().nullable(),
  matched_column: z.enum(E_PART_NUMBER_COLUMNS),
  matched_number: z.string(),
});
export type CatalogNumberMatch = z.infer<typeof CatalogNumberMatchSchema>;

export const CatalogRowRefSchema = z.object({ table: z.enum(E_CATALOG_TABLES), row_id: z.number().int() });
export type CatalogRowRef = z.infer<typeof CatalogRowRefSchema>;

/** GET /matches — 맞은 행들 + 판정. target 은 edit 일 때 그 행 */
export const DraftMatchViewSchema = z.object({
  matches: z.array(CatalogNumberMatchSchema),
  verdict: z.enum(E_DRAFT_MATCH_VERDICTS),
  target: CatalogRowRefSchema.nullable(),
});
export type DraftMatchView = z.infer<typeof DraftMatchViewSchema>;

/** 초안 한 행 — 저장 · 승격이 같은 모양을 보낸다. columns 의 키 = 대상 표 열 이름(평면), 값은 data_type 에 맞춰 변환한 것 */
export type CatalogDraft = {
  option_part: string | null;                           // 승격엔 필수 — 서버가 400
  catalog_table_name: ECatalogTable;
  columns: Record<string, unknown>;
  evidence: Record<string, string>;                     // 모델 칸의 근거는 'model.<열>' 키
  model_columns: Record<string, unknown> | null;        // 모델 표 칸(키 = 모델 표 열 이름). 모델 FK 표만, 아니면 null (2026-10-14 밤 — 요청 하나가 부품 + 모델)
  description_fills: SlotFill[];                        // 사람이 채운 약식 자리 — 원문에서 안 읽힌 필수 자리(2026-10-26 승격도 규칙이). 승격만 읽는다, 임시 저장은 안 남긴다
};

/** POST /close-in-catalog 의 보고 — 닫은 수 · 채운 product 수 · 건너뛴 건(한 건의 예외는 그 건만) */
export const CloseReportSchema = z.object({
  closed: z.number().int(),
  products_filled: z.number().int(),
  skipped: z.array(z.object({ pending_id: z.number().int(), why: z.string() })),
});
export type CloseReport = z.infer<typeof CloseReportSchema>;

export const REQUEST_LIST_LIMIT = 100;                  // 한 묶음

// ─── 호출 ──────────────────────────────────────────────────────────
const BASE = '/api/admin/catalog-requests';

/** GET /?filter&q&period&limit&offset — 요청함 한 묶음(마지막 움직임 순) */
/** GET /lines?q=&table=&limit=&offset= — 카탈로그 전체(확정 행 먼저 PN 순 → 열린 요청). 전체 탭이 그린다(2026-10-20) */
export async function fetchCatalogLines(q: string, tableOrNull: ECatalogTable | null, offset: number): Promise<CatalogLineViewList> {
  const qs = new URLSearchParams({ limit: String(REQUEST_LIST_LIMIT), offset: String(offset) });
  if (q.trim() !== '') qs.set('q', q.trim());
  if (tableOrNull !== null) qs.set('table', tableOrNull);
  return getJson(`${BASE}/lines?${qs.toString()}`, { schema: CatalogLineViewListSchema });
}

export async function fetchCatalogRequests(filter: ECatalogRequestFilter, q: string, period: ERequestPeriod, offset: number): Promise<CatalogRequestViewList> {
  const qs = new URLSearchParams({ filter, limit: String(REQUEST_LIST_LIMIT), offset: String(offset) });
  if (q.trim() !== '') qs.set('q', q.trim());   // 찾기 — 파트넘버 · 디스크립션 · 요청자. 빈 글은 안 보낸다(전부)
  if (period !== 'all') qs.set('period', period);   // 요청날짜 기간 — 전체면 안 보낸다(서버 기본 all)
  return getJson(`${BASE}?${qs.toString()}`, { schema: CatalogRequestViewListSchema });
}

/** GET /?filter&limit=1 — 그 탭의 건수만(total). 사이드바 알람이 쓴다 — 줄은 안 본다 */
export async function fetchCatalogRequestCount(filter: ECatalogRequestFilter): Promise<number> {
  const qs = new URLSearchParams({ filter, limit: '1', offset: '0' });
  const list = await getJson(`${BASE}?${qs.toString()}`, { schema: CatalogRequestViewListSchema, silent: true });   // 전역 '처리 중' 펄 없이
  return list.total;
}

/** GET /:id — 한 건. 없으면 404 → Error */
export async function fetchCatalogRequest(pendingId: number): Promise<CatalogRequestView> {
  return getJson(`${BASE}/${pendingId}`, { schema: CatalogRequestViewSchema });
}

/** GET /columns/:table — 그 표의 칸 목록(필수 여부 · data_type) + 모델 FK 표면 모델 칸. 폼이 그린다 */
export async function fetchTableColumns(table: ECatalogTable): Promise<TableColumnsView> {
  return getJson(`${BASE}/columns/${table}`, { schema: TableColumnsViewSchema });
}

/** GET /columns/:table/model?key= — 열쇠의 모델 행(없으면 row null). 폼이 "모델 있음 / 새로" 를 가른다 */
export async function fetchModelRow(table: ECatalogTable, key: string): Promise<ModelRowView> {
  const qs = new URLSearchParams({ key });
  return getJson(`${BASE}/columns/${table}/model?${qs.toString()}`, { schema: ModelRowViewSchema, silent: true });
}

/** PUT /:id/draft — 사람이 채운 초안을 둔다(requested → drafted · draft_source manual). 승격은 아니다 */
export async function saveCatalogDraft(pendingId: number, draft: CatalogDraft): Promise<CatalogRequestView> {
  return putJson(`${BASE}/${pendingId}/draft`, draft, CatalogRequestViewSchema);
}

/** GET /matches?… — 번호 넷이 맞는 카탈로그 행 + 판정. 빈 번호는 안 보낸다(안 찾음). 전부 비면 묻지 않고 new */
export async function fetchDraftMatches(numbers: DraftNumbers): Promise<DraftMatchView> {
  const qs = new URLSearchParams();
  for (const column of E_PART_NUMBER_COLUMNS) {
    const number = numbers[column];
    if (number !== null && number.trim() !== '') qs.set(column, number.trim());
  }
  if ([...qs.keys()].length === 0) return { matches: [], verdict: 'new', target: null };
  return getJson(`${BASE}/matches?${qs.toString()}`, { schema: DraftMatchViewSchema, silent: true });
}

/** POST /:id/promote — 승격(새 행만 — 옵션 PN 이 카탈로그에 있으면 409, 그땐 [요청 종료]). 필수 칸이 비면 400 → Error */
export async function promoteCatalogPart(pendingId: number, draft: CatalogDraft): Promise<PromoteResult> {
  return postJson(`${BASE}/${pendingId}/promote`, draft, PromoteResultSchema);
}

/** POST /:id/close-in-catalog — [요청 종료]: 이 요청만 연결로 닫는다(카탈로그 한 표에 있을 때만, 아니면 409). 닫은 뒤의 그 줄 */
export async function closeRequestInCatalog(pendingId: number): Promise<CatalogRequestView> {
  return postJson(`${BASE}/${pendingId}/close-in-catalog`, {}, CatalogRequestViewSchema);
}

/** POST /close-in-catalog — 카탈로그에 이미 있는 PN 의 열린 요청 전부를 연결로 닫는다(카탈로그는 안 건드림). 멱등 — 닫을 게 없으면 0 */
export async function closeRequestsInCatalog(): Promise<CloseReport> {
  return postJson(`${BASE}/close-in-catalog`, {}, CloseReportSchema);
}

/** POST /:id/reject — 반려 + 이유(서버 status_reason) */
export async function rejectCatalogPart(pendingId: number, reason: string): Promise<CatalogRequestView> {
  return postJson(`${BASE}/${pendingId}/reject`, { reason }, CatalogRequestViewSchema);
}

/** POST /:id/hold — 보류. 메모가 필요하다(빈 글이면 서버 400) */
export async function holdCatalogPart(pendingId: number, notes: string): Promise<CatalogRequestView> {
  return postJson(`${BASE}/${pendingId}/hold`, { notes }, CatalogRequestViewSchema);
}

/** POST /:id/release — 보류 풀기. 초안 있으면 확인 대기, 없으면 요청됨 */
export async function releaseCatalogPart(pendingId: number): Promise<CatalogRequestView> {
  return postJson(`${BASE}/${pendingId}/release`, {}, CatalogRequestViewSchema);
}

/** PUT /:id/notes — 사람의 운영 메모 한 칸. 빈 글 · null 이면 비움. 어느 상태에서든 */
export async function updateCatalogRequestNotes(pendingId: number, notesOrNull: string | null): Promise<CatalogRequestView> {
  return putJson(`${BASE}/${pendingId}/notes`, { notes: notesOrNull }, CatalogRequestViewSchema);
}

/** POST /:id/research — 조사 시작. 표 없던 요청은 사람이 고른 카테고리를 함께(있으면 null). 요청됨 [지금 조사] · 확인 대기 [다시 조사]. 서버가 루틴을 바로 돌리며 이 번호를 먼저 하라고 한다. 요청됨인 그 줄 */
export async function startCatalogResearch(pendingId: number, tableOrNull: ECatalogTable | null): Promise<CatalogRequestView> {
  return postJson(`${BASE}/${pendingId}/research`, { catalog_table_name: tableOrNull }, CatalogRequestViewSchema);
}

/** 조사 중인가 — requested 이고 카테고리(표)가 있어 루틴 큐에 든 줄. 사람은 기다린다(승격 · 저장 · 반려 · 칸 잠금). 표 없는 requested 는 사람이 표를 골라 저장하는 길이라 아니다 */
export function isResearching(view: CatalogRequestView): boolean {
  return (view.status === 'requested' || view.status === 'pending') && tableOrNullOf(view.catalog_table_name) !== null;
}

function tableOrNullOf(raw: string | null): ECatalogTable | null {
  const found = E_CATALOG_TABLES.find((table) => table === raw);
  return found === undefined ? null : found;
}
